import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  computed,
  effect,
  inject,
  input,
  output,
  viewChild
} from '@angular/core';
import { InputComponent } from '../input/input.component';
import { MaskFieldInputComponent } from '../mask-field-input/mask-field-input.component';
import { MaskFieldInputType } from '../mask-field-input/mask-field-input-type.enum';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IconComponent } from '../icons/icon.component';
import { IconNameType } from '../icons/icon-name-type.enum';
import { CommonModule } from '@angular/common';
import { ConfigService } from '../../core/services/config.service';
import { AuthService } from '../../core/services/auth.service';
import { Account } from '../../core/models/account/account.model';
import { Subscription } from 'rxjs';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { ErrorsService } from '../../core/services/errors.service';
import { SmsSignUpInformationComponent } from '../sms-sign-up-information/sms-sign-up-information.component';
import { SubscriptionsService } from '../../core/models/account/subscriptions-service.model';
import { CustomerSubscription } from '../../core/models/account/customer-subscription.model';
import { ServiceName } from '../../core/models/account/service-name.enum';
import { ChannelName } from '../../core/models/account/channel-name.enum';
import { Utils } from '../../core/utils/utils';
import { ToggleSwitchComponent } from '../toggle-switch/toggle-switch.component';
import { AsyncButtonDirective } from '../directives/async-button.directive';
import { RecaptchaComponent, RecaptchaModule } from 'ng-recaptcha';
import { OrderService } from '../../core/services/order.service';
import { AccountCouponsService } from '../../core/services/account/account-coupons.service';
import { EmbededQuestionnaireFormComponent } from "../embeded-questionnaire-form/embeded-questionnaire-form.component";
import { Questionnaire } from '../../core/models/questionnaire/questionnaire.model';
import { QuestionnaireService } from '../../core/services/questionnaire.service';
import { QuestionnaireAnswer } from '../../core/models/questionnaire/questionnaire-answer.model';
import { QuestionnaireType } from '../../core/models/questionnaire/questionnaire-type.enum';
import { OrderTrackerService } from '../../core/services/order-tracker.service';
import { Router } from '@angular/router';
import { ModalService, ModalName } from '../../core/services/modal.service';
import { GTMService } from '../../core/services/gtm.service';
import { AuditService } from '../../core/services/audit.service';
import { InitParamsService } from '../../core/services/init-params.service';
import { AriaFormDirective } from '../directives/aria-form.directive';
import { CustomValidations } from '../../core/utils/custom-validations';
import { toSignal } from '@angular/core/rxjs-interop';
import { createPasswordCriteriaSignal } from '../../core/utils/pass-criteria';
import { ExternalSignInComponent } from '../external-sign-in/external-sign-in.component';

@Component({
  selector: 'cwo-register-form',
  standalone: true,
  imports: [
    RecaptchaModule,
    InputComponent,
    MaskFieldInputComponent,
    ReactiveFormsModule,
    IconComponent,
    CommonModule,
    TranslocoModule,
    SmsSignUpInformationComponent,
    ToggleSwitchComponent,
    AsyncButtonDirective,
    EmbededQuestionnaireFormComponent,
    AriaFormDirective,
    ExternalSignInComponent
],
  templateUrl: './register-form.component.html',
  styleUrl: './register-form.component.scss',
})
export class RegisterFormComponent implements OnDestroy, OnInit {
  boxShadow = input<string>('');
  submitButton = input<string | undefined>();

  configService = inject(ConfigService);
  authService = inject(AuthService);
  errorsService = inject(ErrorsService);
  modalService = inject(ModalService);
  orderService = inject(OrderService);
  accountCouponsService = inject(AccountCouponsService);
  questionnaireService = inject(QuestionnaireService);
  gtmService = inject(GTMService);
  initParams = inject(InitParamsService);
  private auditService = inject(AuditService);
  private translocoService = inject(TranslocoService);

  storeConfig = computed(() => this.configService.configState().store);
  loyaltyPlan = computed(() => this.configService.loyaltyState());
  isOnModal = computed(() => this.modalService.isModalOpen(ModalName.SignIn));

  order = inject(OrderTrackerService).trackingOrder;

  questionnaire?: Questionnaire;

  readonly recaptchaView = viewChild(RecaptchaComponent);

  readonly personalDetailsCompleted = output();

  allErrors?: string[] = [];
 
  maskFieldInputType = MaskFieldInputType;
  registerForm = new FormGroup({
    firstName: new FormControl('', Validators.required),
    lastName: new FormControl(
      '',
      this.storeConfig()?.needsLastName ? Validators.required : undefined
    ),
    phone: new FormControl(
      '',
      Validators.compose([
        Validators.required,
        Validators.minLength(10),
        Validators.maxLength(10),
      ])
    ),
    email: new FormControl(
      '',
      Validators.compose([Validators.required, Validators.email])
    ),
    password: new FormControl(
      '',
      Validators.compose([
        Validators.required,
        Validators.minLength(12),
        Validators.maxLength(64),
        CustomValidations.uppercase(),
        CustomValidations.digit(),
      ])
    ),
    signUpForLoyalty: new FormControl(true),
    subscribed: new FormControl(true), //email_offers subscription
    smsMarketing: new FormControl(false), //sms subscription
    pushMarketing: new FormControl(false), //push subscription
    acceptTerms: new FormControl(false, Validators.requiredTrue),
    recaptcha: this.storeConfig()?.skipRegistrationRecaptcha ? new FormControl() : new FormControl('', Validators.required)
  });

  loadingSubscription: Subscription | null = null;
  showErrors = false;
  iconNameType = IconNameType;
  subscriptionsString = '';
  validationMessages: any = {
    firstName: {
      required: 'First name is required.',
    },
    lastName: {
      required: 'Last name is required.',
    },
    phone: {
      required: 'Phone is required.',
      minlength: 'Phone Number is invalid.',
      maxlength: 'Phone Number is invalid.',
    },
    email: {
      required: 'Email is required.',
      email: 'Email is invalid.',
    },
    password: {
      required: this.translocoService.translate('password_requirements.required'),
      minlength: this.translocoService.translate('password_requirements.minmax_length'),
      maxlength: this.translocoService.translate('password_requirements.minmax_length'),
      digit: this.translocoService.translate('password_requirements.digit'),
      capitalLetter: this.translocoService.translate('password_requirements.uppercase'),
    },
    acceptTerms: {
      required: 'Age verification is required.',
    },
    recaptcha: {
      required: 'Recaptcha is required.',
    },
  };

  private passwordValueSignal = toSignal(
    this.registerForm.controls.password.valueChanges,
    { initialValue: this.registerForm.controls.password.value}
  );


  passwordCriteria = createPasswordCriteriaSignal(this.passwordValueSignal);

  get firstName() {
    return this.registerForm.controls.firstName;
  }
  get lastName() {
    return this.registerForm.controls.lastName;
  }
  get phone() {
    return this.registerForm.controls.phone;
  }
  get email() {
    return this.registerForm.controls.email;
  }
  get password() {
    return this.registerForm.controls.password;
  }
  get acceptTerms() {
    return this.registerForm.controls.acceptTerms;
  }
  get recaptcha() {
    return this.registerForm.controls.recaptcha;
  }
  get subscribed() {
    return this.registerForm.controls.subscribed;
  }
  get smsMarketing() {
    return this.registerForm.controls.smsMarketing;
  }
  get pushMarketing() {
    return this.registerForm.controls.pushMarketing;
  }

  get signUpForLoyaltyLabel() {
    const loyaltyPlan = this.loyaltyPlan();
    const storeConfig = this.storeConfig();
    
    if (loyaltyPlan?.signupBonus || storeConfig?.registrationSpecial) {
      if (loyaltyPlan?.signupBonus) {
        return this.translocoService.translate(
          storeConfig?.registrationSpecial
            ? "shared_components.signing_forms.registration_form.sign_up_for_with_sign_up_bonus_plus"
            : "shared_components.signing_forms.registration_form.sign_up_for_with_sign_up_bonus",
          {
            loyaltyPlanName: loyaltyPlan?.loyaltyPlanName,
            points: loyaltyPlan?.signupBonus
          }
        );
      } else {
        return this.translocoService.translate(
          "shared_components.signing_forms.registration_form.sign_up_for_with_sign_up_bonus_special",
          { loyaltyPlanName: loyaltyPlan?.loyaltyPlanName }
        );
      }
    } else {
      return this.translocoService.translate(
        "shared_components.signing_forms.registration_form.sign_up_for_general",
        { loyaltyPlanName: loyaltyPlan?.loyaltyPlanName }
      );
    }            
  }

  private el = inject(ElementRef);
  private router = inject(Router);

  constructor() {
    effect(() => {
      this.subscriptionsString = this.calcSubscriptionsString();
    });
  }

  ngOnDestroy(): void {
    this.loadingSubscription?.unsubscribe();
    this.initParams.clearParam([
        'firstName',
        'lastName',
        'phone',
        'subscribed',
        'smsMarketing',
        'push',
        'signUpForLoyalty',
    ]);
  }

  ngOnInit(): void {
    this.questionnaireService.fetchQuestionnaire(QuestionnaireType.REGISTER).subscribe((questionnaire) => {
      this.questionnaire = questionnaire;
    });

    this.autoFillFromUrlParams();

    if(this.router.url.includes('checkout/enjoy')) {
      this.email.setValue(this.order()?.customer?.email ?? '');
      this.phone.setValue(this.order()?.customer?.phone?.replace(/\D/g, '') ?? '');
      this.firstName.setValue(this.order()?.customer?.firstName ?? '');
      this.lastName.setValue(this.order()?.customer?.lastName ?? '');
    }
  }

  calcSubscriptionsString() {
    const subscriptionsArray: string[] = [];

    subscriptionsArray.push('Email');
    if (this.storeConfig()?.supportsSmsSubscriptions) {
      subscriptionsArray.push('SMS');
    }
    if (this.storeConfig()?.pushNotificationCampaigns) {
      subscriptionsArray.push('Push');
    }
    return subscriptionsArray
      .map((value, index) => {
        if (index === subscriptionsArray.length - 1) {
          return value;
        } else {
          return `${value} & `;
        }
      })
      .join('');
  }
  enableErrors() {
    this.showErrors = true;
  }

  onSubmit() {
    this.auditService.createAudit(() => `${this.submitButton() ?? 'Register'} in Registration form button clicked`);

    this.showErrors = false;
    this.allErrors = [];
    if (!this.registerForm.valid) {
      this.auditService.sendNativeFormErrorsToAudits(this.registerForm, this.validationMessages, 'Register');
      this.showErrors = true;
      //focus on the first invalid field!
      //adding timeout to gives invalid class time to be applied
      setTimeout(() => {
        const invalidElements =
          this.el.nativeElement.querySelectorAll('.invalid');

        if (invalidElements.length > 0) {
          invalidElements[0].scrollIntoView();
        }
      }, 100);
      return;
    }

    const newAccount = new Account();
    newAccount.firstName = this.registerForm.controls.firstName.value ?? '';
    newAccount.lastName = this.registerForm.controls.lastName.value ?? '';
    newAccount.phone = this.registerForm.controls.phone.value ?? '';
    newAccount.phone = newAccount.phone?.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') ?? '';
    newAccount.email = this.registerForm.controls.email.value ?? '';
    newAccount.password = this.registerForm.controls.password.value ?? '';
    newAccount.signUpForLoyalty = this.registerForm.controls.signUpForLoyalty.value ?? false;
    newAccount.subscribed = this.registerForm.controls.subscribed.value ?? false;
    newAccount.smsMarketing = this.registerForm.controls.smsMarketing.value ?? false;
    newAccount.acceptTerms = this.registerForm.controls.acceptTerms.value ?? false;
    newAccount.recaptcha = this.registerForm.controls.recaptcha.value;
    this.updateSubscriptionData(newAccount);

    if (this.questionnaire) {
      this.questionnaire.email = newAccount.email;
      newAccount.questionnaireAnswer = QuestionnaireAnswer.fromQuestionnaire(this.questionnaire);
    }

    this.loadingSubscription = this.authService.register(newAccount)
    .subscribe({
      next: (account) => {
        this.gtmService.pushSignUpEvent();
        this.loadingSubscription = null;
        this.personalDetailsCompleted.emit();
        this.accountCouponsService.reloadCoupons();

        this.orderService.fetchOrder().subscribe(()=>{
          const customerForOrder = this.orderService.order().customer;
          if(customerForOrder) this.orderService.auditOrderCustomerUpdate(customerForOrder)
        });
        
        this.auditService.createAudit(() => `Account ${account.email} has been created`);
        if(this.authService.state().isLoggedIn) this.auditService.createAudit(() => `User ${account.email} has logged in`);
                    
        this.modalService.closeModal(ModalName.SignIn).subscribe({
          next: () => {
            setTimeout(() => {
              if (!account?.verifiedEmail) {
                this.modalService.openModal(ModalName.VerifyEmail);
              }
            },200);
          }
        });        
      },
      error: (err) => {
        this.recaptchaView()?.reset();
        this.loadingSubscription = null;
        if (!err.error) {
          this.allErrors = ['Network error'];
        } else {
          const parsedError = this.errorsService.parseErrorResponse(err);
          this.allErrors = parsedError.allErrors;
          this.auditService.createAudit(() => `Registration failed for user with email: ${this.registerForm.value.email} with error: ${parsedError.allErrors}`);
        }
        this.el.nativeElement.querySelectorAll('.primary-group')[0].scrollTo({top: 0, behavior: 'smooth'});
      },
    });
  }

  public recaptchaResolved(value: string | null): void {
    this.recaptcha.setValue(value);
  }

  private updateSubscriptionData(account: Account) {
    if (!Utils.isNullOrUndefined(account.subscribed)) {
      this.setServiveSubscriptionsPerChannel(ServiceName.OFFERS, ChannelName.EMAIL, account.subscribed, account);
    }
    if (!Utils.isNullOrUndefined(account.smsMarketing)) {
      this.setServiveSubscriptionsPerChannel(ServiceName.OFFERS, ChannelName.SMS, account.smsMarketing, account);
    }
    if (this.registerForm.controls.pushMarketing.value) {
      this.setServiveSubscriptionsPerChannel(ServiceName.OFFERS, ChannelName.PUSH_NOTIFICATIONS, this.registerForm.controls.pushMarketing.value, account);
    }
  }

  private setServiveSubscriptionsPerChannel(service: ServiceName, channel: ChannelName, subscribe?: boolean, account?: Account) {
    const subService: SubscriptionsService = new SubscriptionsService().deserialize({ serviceName: service, subscribed: subscribe });
    const subChannel: CustomerSubscription = new CustomerSubscription().deserialize({ channelName: channel, services: [] });
    subChannel.services && subChannel.services.push(subService);
    account?.subscriptions && account.subscriptions.push(subChannel);
  }

  private autoFillFromUrlParams(): void {
    const params = Object.assign({}, this.router.parseUrl(this.router.url).queryParams);
    params['pushMarketing'] = params['push'];
    
    if (
      !Object.keys(params).some(key => Object.keys(this.registerForm.controls).includes(key)) &&
      !params['name'] &&
      !params['hasAgreed']
    ) {
      return;
    }

    delete params['password'];
  
    params['acceptTerms'] = true;
    
    if (params['name']) {
      const splitName = params['name'].split(' ');
      delete params['name'];
      params['firstName'] = splitName[0] || '';
      params['lastName'] = splitName[1] || '';
    }

    const hasAgreed = params['hasAgreed'] === 'true';
    if (hasAgreed) {
      params['subscribed'] = !this.storeConfig()?.hasThirdPartyLoyalty;
      params['smsMarketing'] = !this.storeConfig()?.hasThirdPartyLoyalty && this.storeConfig()?.supportsSmsSubscriptions;
      params['signUpForLoyalty'] = !!this.loyaltyPlan()?.id;
    }

    // Convert string boolean values to actual booleans for known boolean fields
    ['subscribed', 'smsMarketing', 'pushMarketing', 'signUpForLoyalty'].forEach(field => {
      if (params[field] !== undefined) {
        params[field] = params[field] === 'true';
      }
    });

    this.registerForm.patchValue(params);
  }
}
